/**
 * Life4Earth V2 — EVM-Lite API Adapter
 *
 * Endpoint: /api/projects/{projectId}/evm
 *
 * Provides typed fetch functions for EVM (Earned Value Management) data.
 * Used by the BudgetTab's EVM dashboard to render CPI/SPI gauges,
 * S-curve charts, and variance analysis tables.
 */

import { apiFetch } from "./client";

// ── API Response Types ──────────────────────────────────────

export interface EvmPhaseBreakdownResponse {
  phase: string;
  estimated: number;
  pv: number;
  ev: number;
  ac: number;
  cpi: number;
  spi: number;
  cv: number;
  sv: number;
  itemCount: number;
}

export interface EvmCategoryBreakdownResponse {
  category: string;
  estimated: number;
  pv: number;
  ev: number;
  ac: number;
  cpi: number;
  spi: number;
  cv: number;
  sv: number;
  itemCount: number;
}

export interface EvmResponse {
  projectId: string;
  reportingDate: string;
  bac: number;
  pv: number;
  ev: number;
  ac: number;
  cpi: number;
  spi: number;
  cv: number;
  sv: number;
  eac: number;
  etc: number;
  vac: number;
  tcpi: number;
  contingencyPercent: number;
  contingencyAmount: number;
  totalEstimated: number;
  itemCount: number;
  linkedItemCount: number;
  phaseBreakdown: EvmPhaseBreakdownResponse[];
  categoryBreakdown: EvmCategoryBreakdownResponse[];
}

export interface EvmSnapshotResponse {
  snapshotWritten: boolean;
  evm: EvmResponse;
}

// ── Typed frontend shapes ───────────────────────────────────

export interface EvmData {
  projectId: string;
  reportingDate: string;
  bac: number;
  pv: number;
  ev: number;
  ac: number;
  cpi: number;
  spi: number;
  cv: number;
  sv: number;
  eac: number;
  etc: number;
  vac: number;
  tcpi: number;
  contingencyPercent: number;
  contingencyAmount: number;
  totalEstimated: number;
  itemCount: number;
  linkedItemCount: number;
  phaseBreakdown: EvmPhaseBreakdownResponse[];
  categoryBreakdown: EvmCategoryBreakdownResponse[];
}

// ── Shape converters ────────────────────────────────────────

function toEvmData(r: EvmResponse): EvmData {
  return {
    projectId: r.projectId,
    reportingDate: r.reportingDate,
    bac: r.bac,
    pv: r.pv,
    ev: r.ev,
    ac: r.ac,
    cpi: r.cpi,
    spi: r.spi,
    cv: r.cv,
    sv: r.sv,
    eac: r.eac,
    etc: r.etc,
    vac: r.vac,
    tcpi: r.tcpi,
    contingencyPercent: r.contingencyPercent,
    contingencyAmount: r.contingencyAmount,
    totalEstimated: r.totalEstimated,
    itemCount: r.itemCount,
    linkedItemCount: r.linkedItemCount,
    phaseBreakdown: r.phaseBreakdown ?? [],
    categoryBreakdown: r.categoryBreakdown ?? [],
  };
}

// ── API Functions ───────────────────────────────────────────

/**
 * Fetch EVM metrics for a project as of a given date.
 * @param projectId Project GUID
 * @param date Optional reporting date (YYYY-MM-DD). Defaults to today on the server.
 */
export async function fetchEvmData(
  projectId: string,
  date?: string,
): Promise<EvmData> {
  const params = date ? `?date=${date}` : "";
  const json = await apiFetch<EvmResponse>(`/api/projects/${projectId}/evm${params}`);
  return toEvmData(json);
}

/**
 * Write an EVM snapshot (populates schedule_series_points and finance_series_points).
 * Returns the computed EVM data and whether the snapshot was actually written.
 */
export async function writeEvmSnapshot(
  projectId: string,
  date?: string,
): Promise<{ snapshotWritten: boolean; evm: EvmData }> {
  const params = date ? `?date=${date}` : "";
  const json = await apiFetch<EvmSnapshotResponse>(
    `/api/projects/${projectId}/evm/snapshot${params}`,
    { method: "POST" },
  );
  return {
    snapshotWritten: json.snapshotWritten,
    evm: toEvmData(json.evm),
  };
}

// ── Task-level drill-down ───────────────────────────────────

export interface EvmTaskBreakdown {
  preflightItemId: string;
  ganttTaskId: string;
  label: string;
  taskName: string;
  phase: string;
  category: string;
  estimated: number;
  pv: number;
  ev: number;
  ac: number;
  cpi: number;
  spi: number;
  cv: number;
  sv: number;
  percentComplete: number;
}

/**
 * Fetch per-task EVM breakdown for drill-down.
 * Only returns items linked to Gantt tasks.
 */
export async function fetchEvmTaskBreakdown(
  projectId: string,
  date?: string,
): Promise<EvmTaskBreakdown[]> {
  const params = date ? `?date=${date}` : "";
  return apiFetch<EvmTaskBreakdown[]>(`/api/projects/${projectId}/evm/tasks${params}`);
}
